import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProjectContext } from "../../ProjectContext";
import { ConnectButton, useConnectModal } from "@rainbow-me/rainbowkit";
import { ethers } from "ethers";

function LoginBox() {
  const { link_ref_address } = useParams();
  const { account, dbuser, toastSuccess, toastError, setRefAddress, settings, CheckReffaral } = useContext(ProjectContext);
  const [reff_address, setreff_address] = useState(null);
  const { openConnectModal } = useConnectModal();
  const [isProcessing, setIsProcessing] = useState(false);  // Track processing state
  const [error, setError] = useState("");  // To show error messages
  const [showLoginButton, setShowLoginButton] = useState(false);  // To show login button if needed
  const [showError, setShowError] = useState(false);  // To show error message (Backer link not found)
  const navigate = useNavigate();

  // Handle user login and referral address
  useEffect(() => {
    setIsProcessing(true); // Set processing when checking conditions

    if (dbuser !== null) {
      // Scenario 1: If the connected address is found in dbuser.address, proceed
      if (dbuser?.address.toLowerCase() === account?.toLowerCase()) {
        setIsProcessing(false); // Stop processing
        navigate("/dashboard"); // Redirect to dashboard if user is found
        return;
      }
    }

    // Scenario 2: If the connected address is not found and the referral address is missing
    if (!link_ref_address) {
      setIsProcessing(false); // Stop processing
      setShowError(true); // Show error message when backer link is not found
      return;
    }

    // Scenario 3: If connected address is not found, but referral address is present
    if (account?.toLowerCase() !== dbuser?.address.toLowerCase()) {
      setreff_address(link_ref_address); // Set the referral address
      setShowLoginButton(true); // Show the log-in button
    }

    setIsProcessing(false); // Stop processing once all conditions are checked
  }, [link_ref_address, dbuser, account, navigate]);

  // Extract referral address from the URL path
  useEffect(() => {
    const extractedAddress = window.location.pathname.split('/').pop();
    setRefAddress(extractedAddress);  // Store the extracted address
  }, [setRefAddress]);

  // Function to handle login on button click
  const handleOnClick = async () => {
    setIsProcessing(true);  // Start processing state

    try {
      // Check referral validity
      const referralCheck = await CheckReffaral();

      if (referralCheck && referralCheck.code === 400) {
        toastError('Referral is not active.');
        return;
      }

      if (referralCheck?.code === 200) {
        // Referral is valid, proceed to dashboard
        navigate("/dashboard");
      } else {
        toastError('Unexpected error with referral check.');
      }
    } catch (error) {
      console.error('Error checking referral:', error);
      toastError('Failed to verify referral address.');
    } finally {
      setIsProcessing(false);  // Stop processing state
    }
  };

  const addCustomToken = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        if (!ethers.utils.isAddress(settings?.dnet)) {
          toastError('Invalid token address');
          return;
        }

        const tokenAdded = await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: settings?.dnet,
              symbol: "DNET",
              decimals: 6,
            },
          },
        });

        if (tokenAdded) {
          toastSuccess('Token added successfully!');
        } else {
          toastError('Token addition failed.');
        }
      } else {
        toastError('MetaMask is not installed.');
      }
    } catch (error) {
      toastError('Error adding token.');
      console.error('Error adding token:', error);
    }
  };

  const addCustomNetwork = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const customNetwork = {
          chainId: '0xa4b1',
          chainName: 'Arbitrum One',
          rpcUrls: ['https://arb1.arbitrum.io/rpc'],
          nativeCurrency: {
            name: 'Ether',
            symbol: 'ETH',
            decimals: 18,
          },
          blockExplorerUrls: ['https://arbiscan.io'],
        };

        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [customNetwork],
        });

        toastSuccess('Arbitrum network added successfully!');
      } else {
        toastError('MetaMask is not installed.');
      }
    } catch (error) {
      console.error('Error adding custom network:', error);
      toastError('Failed to add custom network.');
    }
  };

  return (
    <div className="padding-top container">
      <div className="d-flex justify-content-center row mx-2">
        <div className="col-xl-6 col-md-8 mb-4 box-login-size">
          <div className="text-white text-center rounded shadow-lg">
            <img
              width={150}
              alt=""
              src="../assets/images/logo/logo-light.svg"
              className="mb-5 img-fluid"
            />
            <br />
            <div className="d-flex justify-content-center">
              {showError ? (
                <h3>Your Backer Link Not Found</h3> // Show error when no referral link is found
              ) : isProcessing ? (
                <div className="processing-message">Processing...</div> // Show processing message when checking
              ) : showLoginButton ? (
                <button className="button-1 px-5 my-2 my-lg-0" onClick={handleOnClick}>
                  <div className="dots_border" />
                  <img
                    className="sparkle img-fluid"
                    alt="lock-icon"
                    src="../assets/images/lock-icon.png"
                  />
                  <span className="text_button">Log in</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openConnectModal}
                  className="button-1 px-5 my-2 my-lg-0 text-white"
                >
                  <span className="text_button">Connect Wallet</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center">
        <button className="button-1 px-5 my-2 my-lg-0 mr-3" onClick={addCustomToken}>
          <div className="dots_border" />
          <span className="text_button">Add Token</span>
        </button>
        <button className="button-1 px-5 my-2 my-lg-0 ml-2" onClick={addCustomNetwork}>
          <div className="dots_border" />
          <span className="text_button">Add Chain</span>
        </button>
      </div>
    </div>
  );
}

export default LoginBox;
